import axios from 'axios';
import i18n, { resetLocale } from '../i18n';
import profileService from './profileService';
import {
  saveAuthToken,
  saveRefreshToken,
  saveUserData,
  getAuthToken,
  getRefreshToken,
  clearAuthData
} from '../utils/storage';
import { API_BASE_URL, APP_CONFIG } from '../utils/constants';
import { extractApiError } from '../utils/apiError';

/**
 * Axios instance configured for authentication-related requests
 * - Sets baseURL from project constants and default json headers
 * - Uses APP_CONFIG.REQUEST_TIMEOUT for request timeout
 */
// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: APP_CONFIG.REQUEST_TIMEOUT,
});

// Request interceptor to add auth token to requests
/**
 * Attach stored auth token to outgoing requests, when available.
 * - Uses `getAuthToken()` to read the token from storage utilities.
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
/**
 * Intercepts responses to handle 401 Unauthorized errors by attempting
 * a token refresh using the refresh token, retrying the original request
 * with a new access token when successful. If refresh fails, clears
 * auth data and redirects to the login page.
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 (Unauthorized) and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          // Save new tokens
          saveAuthToken(accessToken);
          if (newRefreshToken) {
            saveRefreshToken(newRefreshToken);
          }

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear all auth data and redirect to login
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Authentication service class
 */
class AuthService {
  /**
   * Decode JWT token to extract user information
   * @param {string} token - JWT token
   * @returns {object|null} User data from token or null if invalid
   */
  decodeJWTUser(token) {
    try {
      if (!token) return null;
      
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      // Extract user information from JWT payload
      return {
        id: decoded.user_id,
        email: decoded.sub,
        role: decoded.user_role,
        // Add other fields as needed
      };
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {boolean} rememberMe - Whether to persist login (localStorage vs sessionStorage)
   * @returns {Promise<object>} Login response with user data and tokens
   */
  async login(identifier, password, rememberMe = false) {
    try {
      // Create form data for application/x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('username', identifier);
      formData.append('password', password);
      
      const response = await apiClient.post('/api/v1/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      });
      
      // Handle different response formats from backend
      let user, access_token, refresh_token;
      
      if (response.data.user) {
        // Backend sends user data directly
        ({ user, access_token, refresh_token } = response.data);
      } else if (response.data.access_token) {
        // Backend only sends tokens, decode JWT for user info
        access_token = response.data.access_token;
        refresh_token = response.data.refresh_token;
        
        // Decode JWT to extract user information
        user = this.decodeJWTUser(access_token);
      } else {
        // Fallback to old naming convention
        access_token = response.data.accessToken || response.data.access_token;
        refresh_token = response.data.refreshToken || response.data.refresh_token;
        user = response.data.user || this.decodeJWTUser(access_token);
      }

      // Store tokens and user data based on rememberMe preference
      saveAuthToken(access_token, rememberMe);
      saveUserData(user, rememberMe);
      
      if (refresh_token) {
        saveRefreshToken(refresh_token, rememberMe);
      }

      return {
        success: true,
        user,
        accessToken: access_token,
        refreshToken: refresh_token,
      };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      // Extract error message from response
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Register new user
   * @param {object} userData - User registration data
   * @returns {Promise<object>} Registration response
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/api/v1/auth/register', userData);

      const { user, accessToken, refreshToken } = response.data;

      // Store tokens and user data
      saveAuthToken(accessToken);
      saveUserData(user);
      
      if (refreshToken) {
        saveRefreshToken(refreshToken);
      }

      return {
        success: true,
        user,
        accessToken,
        refreshToken,
      };
    } catch (error) {
      console.error('Registration error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Logout user
   * @returns {Promise<object>} Logout response
   */
  async logout() {
    try {
      const refreshToken = getRefreshToken();
      
      // Call logout endpoint if refresh token exists
      if (refreshToken) {
        await apiClient.post('/api/v1/auth/logout', {
          refreshToken,
        });
      }

      // Clear all stored auth data
      clearAuthData();
      // Clear per-user caches/language so the next user starts clean.
      try { profileService.clearCache(); } catch (e) { /* ignore */ }
      try { resetLocale(); } catch (e) { /* ignore */ }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Logout error:', error);

      // Clear auth data even if logout API call fails
      clearAuthData();
      try { profileService.clearCache(); } catch (e) { /* ignore */ }
      try { resetLocale(); } catch (e) { /* ignore */ }

      return {
        success: true, // Still consider it successful since we cleared local data
      };
    }
  }

  /**
   * Validate if the current stored token is valid
   * @returns {boolean} True if token exists and is not expired
   */
  isTokenValid() {
    try {
      const token = getAuthToken();
      if (!token) {
        return false;
      }

      // Decode JWT to check expiration
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiration = decoded.exp;
      
      if (tokenExpiration && tokenExpiration < currentTime) {
        console.log('Token has expired');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated (locally, without API call)
   * @returns {object} Authentication status and user data
   */
  checkAuthStatus() {
    try {
      const token = getAuthToken();
      if (!token) {
        console.log('No token found in storage');
        return {
          isAuthenticated: false,
          user: null,
          needsRefresh: false
        };
      }

      if (!this.isTokenValid()) {
        console.log('Token found but invalid/expired');
        return {
          isAuthenticated: false,
          user: null,
          needsRefresh: false
        };
      }

      // Get stored user data
      const userData = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
      let user = null;
      
      if (userData) {
        user = JSON.parse(userData);
        console.log('Found stored user data:', user.email || user.id);
      } else {
        // Fallback: decode user from JWT
        user = this.decodeJWTUser(token);
        console.log('Decoded user from token:', user?.email || user?.id);
      }

      // Ensure the user always carries a role. Stored user_data may predate the
      // role field (or omit it), which would leave the app stuck on "Loading
      // session..." because role context never resolves. Backfill from the JWT.
      if (user && !user.role) {
        const decoded = this.decodeJWTUser(token);
        if (decoded?.role) {
          user = { ...user, role: decoded.role };
        }
      }

      console.log('Token validation successful, user authenticated locally');
      return {
        isAuthenticated: true,
        user,
        needsRefresh: false
      };
    } catch (error) {
      console.error('Error checking auth status:', error);
      return {
        isAuthenticated: false,
        user: null,
        needsRefresh: false
      };
    }
  }


  /**
   * Update user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<object>} Password update response
   */
  async updatePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.put('/api/v1/auth/change-password', {
        currentPassword,
        newPassword,
      });

      return {
        success: true,
        message: response.data.message || i18n.t('serviceMessages.auth.passwordUpdated'),
      };
    } catch (error) {
      console.error('Password update error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<object>} Password reset response
   */
  async requestPasswordReset(identifier) {
    try {
      const response = await apiClient.post('/api/v1/auth/forgot-password', {
        email: identifier,
      });

      return {
        success: true,
        message: response.data.message || i18n.t('serviceMessages.auth.passwordResetEmailSent'),
      };
    } catch (error) {
      console.error('Password reset request error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<object>} Password reset response
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post('/api/v1/auth/reset-password', {
        token,
        newPassword,
      });

      return {
        success: true,
        message: response.data.message || i18n.t('serviceMessages.auth.passwordReset'),
      };
    } catch (error) {
      console.error('Password reset error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

// Export a singleton instance of AuthService and the configured apiClient
const authService = new AuthService();
export default authService;

// Also export the apiClient for other services to use (e.g., to share interceptors)
export { apiClient };
